import {
    SDI12SensorClusterConfigurationBase,
    SDI12SensorClusterConfigurationConnectedSensor,
    SDI12SensorClusterConfigurationDataReadingCommand,
} from "./SDI12SensorClusterConfiguration.js";

import { unit } from "mathjs";

const inch = 0.0254;
const cm = 0.01;

// 235 byte maximum readout size from probes

//  Sentek Drill & Drop
export function generateSentekDrillAndDropConfiguration(
    depthInInches: number,
    {
        hasSalinity = true,
        hasTemperature = true,
    }: {
        hasSalinity: boolean;
        hasTemperature: boolean;
    },
): SDI12SensorClusterConfigurationBase {
    // figure out a name and short name and stuff
    let name = "TriScan";
    let shortName = "TriScan";
    if (!hasSalinity || !hasTemperature) {
        name = "Drill & Drop";
        shortName = "D&D";
    }

    // note that the number of depths does not affect how much data the probe sends. It could if we optimized it better,
    // but we haven't. So it just means a lot of the data it sends back won't be read, but it will still exist. So
    // number of depths doesn't affect the sensorDataPositionInBuffer field.
    const numberOfDepths = depthInInches / 4;

    const hasMoreThan8Depths = numberOfDepths > 8;
    const bufferSizeOfSingleDataKind = 9 * (hasMoreThan8Depths ? 16 : 8); // sizes the array to store the datacuts the node sends correctly

    const sizeOfMoistureData = bufferSizeOfSingleDataKind;
    const sizeOfSalinityData = hasSalinity ? bufferSizeOfSingleDataKind : 0;

    // define commands for optional sensors
    const salinityProbesCommands = [
        {
            command: "ADDRC1!",
            delay: 45,
            doCRCValidation: true,
            readResponseDataToBuffer: false,
        },
        {
            command: "ADDRD0!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 73,
        },
        ...(hasMoreThan8Depths
            ? [
                  {
                      command: "ADDRD1!",
                      delay: 5,
                      doCRCValidation: true,
                      readResponseDataToBuffer: true,
                      startOfByteRangeToRead: 2,
                      endOfByteRangeToRead: 37,
                  },
              ]
            : []),
    ] as const satisfies SDI12SensorClusterConfigurationDataReadingCommand[];

    const temperatureProbesCommands = [
        {
            command: "ADDRC2!",
            delay: 20,
            doCRCValidation: true,
            readResponseDataToBuffer: false,
        },
        {
            command: "ADDRD0!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 73,
        },
        ...(hasMoreThan8Depths
            ? [
                  {
                      command: "ADDRD1!",
                      delay: 5,
                      doCRCValidation: true,
                      readResponseDataToBuffer: true,
                      startOfByteRangeToRead: 2,
                      endOfByteRangeToRead: 37,
                  },
              ]
            : []),
    ] as const satisfies SDI12SensorClusterConfigurationDataReadingCommand[];

    // populate and return config
    return {
        manufacturerName: "Sentek Technologies",
        modelName: `Drill & Drop ${hasSalinity && hasTemperature ? "TriScan " : ""}${depthInInches}"`,
        friendlyName: `${name} ${depthInInches}"`,
        friendlyShortName: `${shortName} ${depthInInches}"`,
        connectedSensors: [
            ...[...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => ({
                sensorType: "SentekDrillAndDropSoilMoistureSensor",
                depth: inch * 4 * (i + 1),
                sensorDataLength: 9,
                // probe data goes in order from shallowest to deepest, 9 bytes per reading.
                sensorDataPositionInBuffer: 9 * i,
                encodingType: "ascii",
            })),

            // make sure we only add these if salinity is supported
            ...(!hasSalinity
                ? []
                : [...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => ({
                      sensorType: "SentekDrillAndDropSoilSalinitySensor",
                      depth: inch * 4 * (i + 1),
                      sensorDataLength: 9,
                      // probe data goes in order from shallowest to deepest, 9 bytes per reading, with salinity after
                      // all of the moisture readings.
                      sensorDataPositionInBuffer: 9 * i + sizeOfMoistureData,
                      encodingType: "ascii",
                  }))),

            // make sure we only add these if temperature is supported
            ...(!hasTemperature
                ? []
                : [...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => ({
                      sensorType: "SentekDrillAndDropSoilTemperatureSensor",
                      depth: inch * 4 * (i + 1),
                      sensorDataLength: 9,
                      // probe data goes in order from shallowest to deepest, 9 bytes per reading, with temperature after
                      // all of the moisture and/or salinity readings.  sizeOfMoistureData amd sizeOfSalinityData will be 0
                      // if they are not present.
                      sensorDataPositionInBuffer: 9 * i + sizeOfMoistureData + sizeOfSalinityData,
                      encodingType: "ascii",
                  }))),
        ],
        commandsToGetData: [
            // read soil moisture
            {
                command: "ADDRC!",
                delay: 20,
                doCRCValidation: true,
                readResponseDataToBuffer: false,
            },
            {
                command: "ADDRD0!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: true,
                startOfByteRangeToRead: 2,
                endOfByteRangeToRead: 73,
            },
            ...(hasMoreThan8Depths
                ? [
                      {
                          command: "ADDRD1!",
                          delay: 5,
                          doCRCValidation: true,
                          readResponseDataToBuffer: true,
                          startOfByteRangeToRead: 2,
                          endOfByteRangeToRead: 37,
                      },
                  ]
                : []),

            // read salinity if salinity sensors are present
            ...(!hasSalinity ? [] : salinityProbesCommands),

            // read temperature if temperature sensors are present
            ...(!hasTemperature ? [] : temperatureProbesCommands),
        ],
    };
}

//  EnviroPro
export function generateEnviroProGSeriesConfiguration(
    depthInInches: number,
    {
        hasSalinity = true,
        hasTemperature = true,
    }: {
        hasSalinity: boolean;
        hasTemperature: boolean;
    },
): SDI12SensorClusterConfigurationBase {
    //Round to nearest 10cm to match EnviroPro website
    const depthInCm = Math.floor((depthInInches * 2.54) / 10) * 10;

    // note that the number of depths does not affect how much data the probe sends. It could if we optimized it better,
    // but we haven't. So it just means a lot of the data it sends back won't be read, but it will still exist. So
    // number of depths doesn't affect the sensorDataPositionInBuffer field.
    const numberOfDepths = depthInCm / 10;

    // Apply SDI12 naming pattern
    const name = `EP100G${!hasSalinity ? "L" : ""}-${numberOfDepths.toString(10).padStart(2, "0")} ${hasSalinity ? "EC" : ""}`;
    const shortName = `EP100G${!hasSalinity ? "L" : ""}-${numberOfDepths.toString(10).padStart(2, "0")}`;

    const hasMoreThan8Depths = numberOfDepths > 8;
    const bufferSizeOfSingleDataKind = 7 * (hasMoreThan8Depths ? 16 : 8);
    const sizeOfMoistureData = bufferSizeOfSingleDataKind;
    const sizeOfSalinityData = hasSalinity ? bufferSizeOfSingleDataKind : 0;

    // define commands for optional sensors
    const salinityProbesCommands = [
        {
            command: "ADDRC1!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: false,
        },
        {
            command: "ADDRD0!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 57,
        },
        ...(hasMoreThan8Depths
            ? [
                  {
                      command: "ADDRD1!",
                      delay: 5,
                      doCRCValidation: true,
                      readResponseDataToBuffer: true,
                      startOfByteRangeToRead: 2,
                      endOfByteRangeToRead: 57,
                  },
              ]
            : []),
    ] as const satisfies SDI12SensorClusterConfigurationDataReadingCommand[];

    const temperatureProbesCommands = [
        {
            command: "ADDRC2!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: false,
        },
        {
            command: "ADDRD0!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 57,
        },

        ...(hasMoreThan8Depths
            ? [
                  {
                      command: "ADDRD1!",
                      delay: 5,
                      doCRCValidation: true,
                      readResponseDataToBuffer: true,
                      startOfByteRangeToRead: 2,
                      endOfByteRangeToRead: 57,
                  },
              ]
            : []),
    ] as const satisfies SDI12SensorClusterConfigurationDataReadingCommand[];

    // populate and return config
    return {
        manufacturerName: "EnviroPro",
        modelName: `EnviroProGSeries ${hasSalinity && hasTemperature ? "WithSalinity " : ""}${depthInCm}"`,
        friendlyName: `${name} ${depthInCm}cm / ${depthInInches}"`,
        friendlyShortName: `${shortName} ${depthInInches}"`,
        connectedSensors: [
            ...[...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => ({
                sensorType: "EnviroProMoistureSensor",
                depth: 10 * (i + 1) * cm,
                sensorDataLength: 7,
                // probe data goes in order from shallowest to deepest, 9 bytes per reading.
                sensorDataPositionInBuffer: 7 * i,
                encodingType: "ascii",
            })),

            // make sure we only add these if salinity is supported
            ...(!hasSalinity
                ? []
                : [...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => ({
                      sensorType: "EnviroProSalinitySensor",
                      depth: 10 * (i + 1) * cm,
                      sensorDataLength: 7,
                      // probe data goes in order from shallowest to deepest, 9 bytes per reading, with salinity after
                      // all of the moisture readings.
                      sensorDataPositionInBuffer: 7 * i + sizeOfMoistureData,
                      encodingType: "ascii",
                  }))),

            // make sure we only add these if temperature is supported
            ...(!hasTemperature
                ? []
                : [...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => ({
                      sensorType: "EnviroProTemperatureSensor",
                      depth: 10 * (i + 1) * cm,
                      sensorDataLength: 7,
                      // probe data goes in order from shallowest to deepest, 9 bytes per reading, with temperature after
                      // all of the moisture and/or salinity readings.  sizeOfMoistureData amd sizeOfSalinityData will be 0
                      // if they are not present.
                      sensorDataPositionInBuffer: 7 * i + sizeOfMoistureData + sizeOfSalinityData,
                      encodingType: "ascii",
                  }))),
        ],
        commandsToGetData: [
            // read soil moisture
            {
                command: "ADDRC3!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: false,
            },
            {
                command: "ADDRD0!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: true,
                startOfByteRangeToRead: 2,
                endOfByteRangeToRead: 57,
            },
            ...(hasMoreThan8Depths
                ? [
                      {
                          command: "ADDRD1!",
                          delay: 5,
                          doCRCValidation: true,
                          readResponseDataToBuffer: true,
                          startOfByteRangeToRead: 2,
                          endOfByteRangeToRead: 57,
                      },
                  ]
                : []),

            // read salinity if salinity sensors are present
            ...(!hasSalinity ? [] : salinityProbesCommands),

            // read temperature if temperature sensors are present
            ...(!hasTemperature ? [] : temperatureProbesCommands),
        ],
    } as SDI12SensorClusterConfigurationBase;
}

//  Aquacheck
/* They seem to have 2 models, a "sub-surface" and a "standard" probe, but they seem to be identical in
terms of specs and installation. This integration supports both of these probes */
export function generateAquaCheckConfiguration(
    depthMm: number,
    sensorCount: number,
    sensorSpacingMm: number,
    {
        hasTemperature = true,
    }: {
        hasTemperature: boolean;
    },
): SDI12SensorClusterConfigurationBase {
    // figure out a name and short name and stuff
    const baseName = "AquaCheck";

    const sizeOfMoistureData = 9 * 12;
    const sizeOfTemperatureData = hasTemperature ? 7 * 12 : 0;

    const temperatureProbesCommands = [
        {
            command: "ADDRM1!",
            delay: 3,
            doCRCValidation: true,
            readResponseDataToBuffer: false,
        },
        {
            command: "ADDRD0!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 21,
        },
        {
            command: "ADDRD1!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 21,
        },
    ] as const satisfies SDI12SensorClusterConfigurationDataReadingCommand[];

    const moistureProbesCommands = [
        {
            command: "ADDRM!",
            delay: 3,
            doCRCValidation: true,
            readResponseDataToBuffer: false,
        },
        {
            command: "ADDRD0!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 27,
        },
        {
            command: "ADDRD1!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 27,
        },
    ] as const satisfies SDI12SensorClusterConfigurationDataReadingCommand[];

    // populate and return config
    return {
        manufacturerName: "AquaCheck USA",
        modelName: `${depthMm}mm ${sensorCount}-Sensor (${sensorSpacingMm}mm Spacing) SDI-12 Probe`,
        friendlyName: `${depthMm}mm ${sensorCount}-Sensor Multi-Depth Probe`,
        friendlyShortName: `${baseName} ${depthMm}mm x${sensorCount}`,
        connectedSensors: [
            ...[...new Array(sensorCount)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => ({
                sensorType: "AquaCheckSoilMoistureSensor",
                depth: unit(sensorSpacingMm * i, "mm").toNumber("m"),
                sensorDataLength: sizeOfMoistureData,
                sensorDataPositionInBuffer: 0,
                encodingType: "ascii",
                regexSelection: new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + i + "}([\\+\\-][0-9\\.]+)"),
            })),

            ...(!hasTemperature
                ? []
                : [...new Array(sensorCount)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => ({
                      sensorType: "AquaCheckSoilTemperatureSensor",
                      depth: unit(sensorSpacingMm * i, "mm").toNumber("m"),
                      sensorDataLength: sizeOfTemperatureData,
                      sensorDataPositionInBuffer: 50,
                      encodingType: "ascii",
                      regexSelection: new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + i + "}([\\+\\-][0-9\\.]+)"),
                  }))),
        ],
        commandsToGetData: [
            // read moisture
            ...moistureProbesCommands,

            // read temperature if temperature sensors are present
            ...(!hasTemperature ? [] : temperatureProbesCommands),
        ],
    };
}

/** Hydrascout HSTI Standard Probes (Salinity to be released)
Length/sensor count is variable, will add custom entries when needed, for example, both a 80" and a 32" might have 4 sensors */
export function generateHydrascoutConfiguration(
    depthInInches: number,
    numberSensors: number,
    {
        hasSalinity = false,
        hasTemperature = true,
        isSandy = false,
    }: {
        hasSalinity: boolean;
        hasTemperature: boolean;
        isSandy: boolean;
    },
): SDI12SensorClusterConfigurationBase {
    const name = "Hydrascout";
    const shortName = "Hydrascout";

    const sizeOfMoistureData = 84;
    const sizeOfTemperatureData = hasTemperature ? 66 : 0;

    const salinityProbesCommands = [
        {
            command: "ADDRC3!",
            delay: 45,
            doCRCValidation: true,
            readResponseDataToBuffer: false,
        },
        {
            command: "ADDRD0!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 73,
        },
        {
            command: "ADDRD1!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 37,
        },
    ] as const satisfies SDI12SensorClusterConfigurationDataReadingCommand[];

    const temperatureProbesCommands = [
        {
            command: "ADDRC1!",
            delay: 20,
            doCRCValidation: true,
            readResponseDataToBuffer: false,
        },
        {
            command: "ADDRD0!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 23,
            // each addrDx! command will read 3 sensors (each temp reading is 7 bits)
        },
        {
            command: "ADDRD1!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 23,
        },
        {
            command: "ADDRD2!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 23,
            //
        },
    ] as const satisfies SDI12SensorClusterConfigurationDataReadingCommand[];

    const numberOfDepths = numberSensors;

    // populate and return config
    return {
        manufacturerName: "HSTI",
        modelName: `Hydrascout ${hasSalinity && hasTemperature ? "with Salinity" : ""}${depthInInches}" ${numberSensors} sensor(s) ${isSandy ? "in sandy soil" : ""}`,
        friendlyName: `${name} ${depthInInches}" ${numberSensors} sensor(s) ${isSandy ? "in sandy soil" : ""}`,
        friendlyShortName: `${shortName} ${depthInInches}" ${numberSensors} sensor(s) ${isSandy ? "in sandy soil" : ""}`,

        connectedSensors: [
            ...[...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => {
                const pattern = new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + i + "}([\\+\\-][0-9\\.]+)");

                return {
                    sensorType: isSandy ? "HydrascoutMoistureSensorSandy" : "HydrascoutMoistureSensor", // adds sandy soil offset if isSandy is true
                    depth: (depthInInches / numberSensors) * (i + 1) * inch,
                    // since #sensors and length varies, this evenly distributes each sensor along the lenth of the probe
                    sensorDataLength: sizeOfMoistureData,
                    // probe data goes in order from shallowest to deepest, 9 bytes per reading.
                    sensorDataPositionInBuffer: 0,
                    encodingType: "ascii",
                    regexSelection: pattern,
                };
            }),

            // make sure we only add these if salinity is supported
            ...(!hasSalinity
                ? []
                : [...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => {
                      const pattern = new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + i + "}([\\+\\-][0-9\\.]+)");

                      return {
                          sensorType: "HydrascoutSalinitySensor",
                          depth: (depthInInches / numberOfDepths) * (i + 1) * inch,
                          sensorDataLength: 27,
                          // probe data goes in order from shallowest to deepest, 9 bytes per reading, with salinity after
                          // all of the moisture readings.
                          sensorDataPositionInBuffer: sizeOfMoistureData + sizeOfTemperatureData,
                          encodingType: "ascii",
                          regexSelection: pattern,
                      };
                  })),

            // make sure we only add these if temperature is supported
            ...(!hasTemperature
                ? []
                : [...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => {
                      const pattern = new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + i + "}([\\+\\-][0-9\\.]+)");

                      return {
                          sensorType: "HydrascoutTemperatureSensor",
                          depth: (depthInInches / numberOfDepths) * (i + 1) * inch,
                          // since #sensors and length varies, this evenly distributes each sensor along the lenth of the probe
                          sensorDataLength: sizeOfTemperatureData,
                          // probe data goes in order from shallowest to deepest, 9 bytes per reading, with temperature after
                          // all of the moisture and/or salinity readings.  sizeOfMoistureData amd sizeOfSalinityData will be 0
                          // if they are not present.
                          sensorDataPositionInBuffer: sizeOfMoistureData, //+ sizeOfSalinityData,
                          encodingType: "ascii",
                          regexSelection: pattern,
                      };
                  })),
        ],
        commandsToGetData: [
            // read soil moisture
            {
                command: "ADDRC0!",
                delay: 20,
                doCRCValidation: true,
                readResponseDataToBuffer: false,
            },
            {
                command: "ADDRD0!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: true,
                startOfByteRangeToRead: 2,
                endOfByteRangeToRead: 29,
                // each addrDx! command will read 3 sensors (each moisture reading is 9 bits)
            },
            {
                command: "ADDRD1!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: true,
                startOfByteRangeToRead: 2,
                endOfByteRangeToRead: 29,
            },
            {
                command: "ADDRD2!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: true,
                startOfByteRangeToRead: 2,
                endOfByteRangeToRead: 29,
            },

            // read salinity if salinity sensors are present
            ...(!hasSalinity ? [] : salinityProbesCommands),

            // read temperature if temperature sensors are present
            ...(!hasTemperature ? [] : temperatureProbesCommands),
        ],
    };
}

// Teros 12 Single Depth Sensor
export function generateTeros12Configuration(depthInInches: number): SDI12SensorClusterConfigurationBase {
    // figure out a name and short name and stuff
    const name = `Teros 12 - ${depthInInches}"`;
    const shortName = `Teros 12`;

    // populate and return config
    return {
        manufacturerName: "Meter Group",
        modelName: `Teros 12`,
        friendlyName: `${name}`,
        friendlyShortName: `${shortName}`,

        connectedSensors: [
            {
                sensorType: "Teros12MoistureSensor",
                depth: depthInInches * inch,
                sensorDataLength: 21,
                sensorDataPositionInBuffer: 0,
                encodingType: "ascii",
                regexSelection: new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + 0 + "}([\\+\\-][0-9\\.]+)"),
            },

            {
                sensorType: "Teros12SalinitySensor",
                depth: depthInInches * inch,
                sensorDataLength: 21,
                sensorDataPositionInBuffer: 0,
                encodingType: "ascii",
                regexSelection: new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + 2 + "}([\\+\\-][0-9\\.]+)"),
            },
            {
                sensorType: "Teros12TemperatureSensor",
                depth: depthInInches * inch,
                sensorDataLength: 21,
                sensorDataPositionInBuffer: 0,
                encodingType: "ascii",
                regexSelection: new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + 1 + "}([\\+\\-][0-9\\.]+)"),
            },
        ],

        commandsToGetData: [
            // read soil moisture
            {
                command: "ADDRM!",
                delay: 20,
                doCRCValidation: true,
                readResponseDataToBuffer: false,
            },
            {
                command: "ADDRD0!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: true,
                startOfByteRangeToRead: 1,
                endOfByteRangeToRead: 21,
            },
        ],
    } as SDI12SensorClusterConfigurationBase;
}

/** FloraPulse Microtensiometer
 * Reads the stem water potential of the tree in bars */
export function generateFloraPulseConfiguration(): SDI12SensorClusterConfigurationBase {
    const numberOfDepths = 1; // single point sensor

    // populate and return config
    return {
        manufacturerName: "FloraPulse",
        modelName: "Microtensiometer",
        friendlyName: "FloraPulse Microtensiometer",
        friendlyShortName: "Microtensiometer",
        connectedSensors: [
            ...[...new Array(numberOfDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>((dummy, i) => {
                const pattern = new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + i + "}([\\+\\-][0-9\\.]+)");

                return {
                    sensorType: "FloraPulseMicrotensiometer",
                    depth: 6 * inch, // sensor has a fixed depth of 6 inches
                    sensorDataLength: 21,
                    sensorDataPositionInBuffer: 0,
                    encodingType: "ascii",
                    regexSelection: pattern,
                };
            }),
        ],

        commandsToGetData: [
            // read water potential from probe
            {
                command: "ADDRMC!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: false,
            },
            {
                command: "ADDRD0!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: true,
                startOfByteRangeToRead: 1,
                endOfByteRangeToRead: 21,
            },
        ],
    };
}

//  GroPoint Profile Probes
export function generateGroPointProbesConfiguration(
    depthInCM: number,
    {
        hasTemperature = true,
    }: {
        hasTemperature: boolean;
    },
): SDI12SensorClusterConfigurationBase {
    const name = "GroPoint Profile";
    const shortName = "GPLP";

    const numberOfMoistureDepths = Math.round(depthInCM / 15);

    /*
    GPLP-2 : 4 temp sensors
    GPLP-3, GPLP-4 : 6 temp sensors
    GPLP-5 : 7 temp sensors
    GPLP-6 : 9 temp sensors
    GPLP-8 : 10 temp sensors

    [3.5, 10, 20, 30, 40, 45, 60, 75, 90]  // The temperature sensor distribution of GPLP-3 and GPLP-6 probes
    [3.5, 10, 20, 30, 45, 60, 75, 90, 105, 120]  // The temperature sensor distribution of GPLP-2, 4, 5, 8 probes
    */

    interface Dictionary {
        [key: string]: number; // Index signature
    }

    const numberOfTemperatureSensors: Dictionary = {
        GPLP2: 4,
        GPLP3: 6,
        GPLP4: 6,
        GPLP5: 7,
        GPLP6: 9,
        GPLP8: 10,
    };
    const sensorName = `GPLP${numberOfMoistureDepths}`;
    const numberOfTemperatureDepths = numberOfTemperatureSensors[sensorName];
    if (numberOfTemperatureDepths === undefined) {
        throw new Error(`Unsupported GroPoint probe depth: ${depthInCM}cm (derived ${sensorName})`);
    }

    const needsMoreDataCommands = numberOfMoistureDepths >= 6;
    const needsMoreTemperatureDataCommands = numberOfTemperatureDepths >= 8;
    const moistureDataLengthMain = 32 - 2 + 1;
    const moistureDataLengthExtended = 12 - 2 + 1;
    const temperatureDataLengthMain = 37 - 2 + 1;
    const temperatureDataLengthExtended = 22 - 2 + 1;
    const sizeOfMoistureData = moistureDataLengthMain + (needsMoreDataCommands ? moistureDataLengthExtended : 0);
    const sizeOfTemperatureData =
        temperatureDataLengthMain + (needsMoreTemperatureDataCommands ? temperatureDataLengthExtended : 0);

    const temperatureProbesCommands = [
        {
            command: "ADDRM1!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: false,
        },
        {
            command: "ADDRD0!",
            delay: 5,
            doCRCValidation: true,
            readResponseDataToBuffer: true,
            startOfByteRangeToRead: 2,
            endOfByteRangeToRead: 37,
        },
        ...(needsMoreTemperatureDataCommands
            ? [
                  {
                      command: "ADDRM2!",
                      delay: 5,
                      doCRCValidation: true,
                      readResponseDataToBuffer: false as const,
                  },
                  {
                      command: "ADDRD0!",
                      delay: 5,
                      doCRCValidation: true,
                      readResponseDataToBuffer: true,
                      startOfByteRangeToRead: 2,
                      endOfByteRangeToRead: 22,
                  },
              ]
            : []),
    ] as const satisfies SDI12SensorClusterConfigurationDataReadingCommand[];

    const temperatureDepthArray =
        numberOfMoistureDepths === 3 || numberOfMoistureDepths === 6
            ? [3.5, 10, 20, 30, 40, 45, 60, 75, 90]
            : [3.5, 10, 20, 30, 45, 60, 75, 90, 105, 120];

    // populate and return config
    return {
        manufacturerName: "GroPoint",
        modelName: `GPLP-S-T-${numberOfMoistureDepths}`,
        friendlyName: `${name}-S-T-${numberOfMoistureDepths} ${depthInCM} cm`,
        friendlyShortName: `${shortName}-S-T-${numberOfMoistureDepths}`,

        connectedSensors: [
            ...[...new Array(numberOfMoistureDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>(
                (dummy, i) => {
                    const pattern = new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + i + "}([\\+\\-][0-9\\.]+)");

                    return {
                        sensorType: "GroPointMoistureSensor",
                        depth: cm * 15 * (i + 1),
                        // since #sensors and length varies, this evenly distributes each sensor along the length of the probe
                        sensorDataLength: sizeOfMoistureData,
                        // probe data goes in order from shallowest to deepest, 5 bytes per reading.
                        sensorDataPositionInBuffer: 0,
                        encodingType: "ascii",
                        regexSelection: pattern,
                    };
                },
            ),

            // make sure we only add these if temperature is supported
            ...(!hasTemperature
                ? []
                : [...new Array(numberOfTemperatureDepths)].map<SDI12SensorClusterConfigurationConnectedSensor>(
                      (dummy, i) => {
                          const pattern = new RegExp("(?:[\\+\\-][^\\+\\-]+\u0000*){" + i + "}([\\+\\-][0-9\\.]+)");

                          return {
                              sensorType: "GroPointTemperatureSensor",
                              depth: cm * temperatureDepthArray[i],
                              // since #sensors and length varies, this evenly distributes each sensor along the length of the probe
                              sensorDataLength: sizeOfTemperatureData,
                              // probe data goes in order from shallowest to deepest, with temperature payload after moisture.
                              sensorDataPositionInBuffer: sizeOfMoistureData,
                              encodingType: "ascii",
                              regexSelection: pattern,
                          };
                      },
                  )),
        ],

        commandsToGetData: [
            // read soil moisture
            {
                command: "ADDRM!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: false,
            },
            {
                command: "ADDRD0!",
                delay: 5,
                doCRCValidation: true,
                readResponseDataToBuffer: true,
                startOfByteRangeToRead: 2,
                endOfByteRangeToRead: 32,
            },
            ...(needsMoreDataCommands
                ? [
                      {
                          command: "ADDRD1!",
                          delay: 5,
                          doCRCValidation: true,
                          readResponseDataToBuffer: true,
                          startOfByteRangeToRead: 2,
                          endOfByteRangeToRead: 12,
                      },
                  ]
                : []),

            // read temperature if temperature sensors are present
            ...(!hasTemperature ? [] : temperatureProbesCommands),
        ],
    };
}
